import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Download, Users, Search } from 'lucide-react';
import OperatorSubscribers from './OperatorSubscribers';
import FeeChargeCards from './FeeChargeCards';
import ServiceTypeDetails from './ServiceTypeDetails';
import OperatorServiceBreakdown from './OperatorServiceBreakdown';
import MonthlyTrends from './MonthlyTrends';
import PDFExporter from '../utils/pdfExporter';
import { DEFAULT_REPORT_MONTH, DEFAULT_REPORT_YEAR } from '../utils/reportPeriod';

const CURRENT_YEAR = new Date().getFullYear();

const DashboardSubscriber = () => {
  const { t } = useTranslation();
  const [selectedMonth, setSelectedMonth] = useState(DEFAULT_REPORT_MONTH);
  const [selectedYear, setSelectedYear] = useState(DEFAULT_REPORT_YEAR);
  const [appliedMonth, setAppliedMonth] = useState(DEFAULT_REPORT_MONTH);
  const [appliedYear, setAppliedYear] = useState(DEFAULT_REPORT_YEAR);
  const [isExporting, setIsExporting] = useState(false);

  const handleSearchClick = () => {
    setAppliedMonth(selectedMonth);
    setAppliedYear(selectedYear);
  };

  // Month options for filtering
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  // PDF Export Handler
  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const selectedMonthName = months.find(m => m.value === appliedMonth)?.label || 'Current';
      const filename = `subscriber-dashboard-${selectedMonthName.toLowerCase()}-${appliedYear}`;

      const result = await PDFExporter.exportToPDF(
        'subscriber-dashboard',
        filename,
        {
          scale: 1.5,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        }
      );

      if (result.success) {
        console.log('PDF exported successfully');
      } else {
        console.error('PDF export failed:', result.error);
        alert('Failed to export PDF: ' + result.error);
      }
    } catch (error) {
      console.error('PDF export error:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <main id="subscriber-dashboard" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-green-600 mr-4" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('dashboardSubscriber.title')}</h1>
              <p className="text-sm md:text-base text-gray-600">{t('dashboardSubscriber.subtitle')}</p>
            </div>
          </div>
          <div className="flex flex-row flex-wrap items-center gap-2 sm:gap-3">
            <Calendar className="h-4 w-4 text-gray-500 shrink-0" />
            <label className="text-sm text-gray-600 shrink-0">{t('common.month')}:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white shrink-0"
              aria-label={t('common.month')}
            >
              {months.map(month => (
                <option key={month.value} value={month.value}>
                  {t(`common.monthNames.${month.value}`)}
                </option>
              ))}
            </select>
            <label className="text-sm text-gray-600 shrink-0">{t('common.year')}:</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white shrink-0"
              aria-label={t('common.year')}
            >
              {Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleSearchClick}
              className="shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 whitespace-nowrap min-w-[100px]"
            >
              <Search className="h-4 w-4" />
              {t('common.search')}
            </button>
            <button
              type="button"
              onClick={handleExportPDF}
              disabled={isExporting}
              className={`shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap min-w-[100px] ${isExporting
                ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500'
              }`}
            >
              <Download className="h-4 w-4" />
              <span>{isExporting ? t('common.exporting') : t('common.exportPDF')}</span>
            </button>
          </div>
        </div>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        {t('common.showingDataFor')}: <strong>{t(`common.monthNames.${appliedMonth}`)} {appliedYear}</strong>
      </p>

      {/* All sections use appliedMonth/appliedYear so changing month + Search updates everything */}
      <div key={`subscriber-dashboard-${appliedMonth}-${appliedYear}`} className="space-y-8">
        <div>
          <OperatorSubscribers month={appliedMonth} year={appliedYear} />
        </div>
        <div>
          <FeeChargeCards month={appliedMonth} year={appliedYear} />
        </div>
        <div>
          <ServiceTypeDetails month={appliedMonth} year={appliedYear} />
        </div>
        <div>
          <OperatorServiceBreakdown month={appliedMonth} year={appliedYear} />
        </div>
        <div>
          <MonthlyTrends type="subscriber" month={appliedMonth} year={appliedYear} />
        </div>
      </div>
    </main>
  );
};

export default DashboardSubscriber;